import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useToast } from '../../ui/Toast';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';

/**
 * FetchWizard — assistente de 3 passos para cadastrar um provider `fetch` (ADR-0009):
 *  1. Mapear o que ENVIAMOS (endpoint, método, auth, query/headers/body com tokens).
 *  2. EXECUTAR (preview-fetch) e ver o JSON cru que chega.
 *  3. MAPEAR a resposta (list_path + item_mapping: qual campo é a URL do arquivo, etc.).
 */
const FILE_TYPES = ['image', 'video', 'audio', 'document', 'presentation', 'spreadsheet', 'data', 'other'];

interface KV {
  k: string;
  v: string;
}
function kvToObj(rows: KV[]): Record<string, string> {
  const o: Record<string, string> = {};
  for (const r of rows) if (r.k.trim()) o[r.k.trim()] = r.v;
  return o;
}

const inputStyle: CSSProperties = {
  padding: '7px 10px',
  fontSize: 13,
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-sm)',
  background: 'var(--bg-0)',
  color: 'var(--text-0)',
  outline: 'none',
  width: '100%'
};
const labelStyle: CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4, display: 'block' };

function KVEditor({ rows, onChange, label }: { rows: KV[]; onChange: (r: KV[]) => void; label: string }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((row, i) => (
          <div key={i} style={{ display: 'flex', gap: 6 }}>
            <input
              placeholder="chave"
              value={row.k}
              onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, k: e.target.value } : r)))}
              style={{ ...inputStyle, flex: '0 0 34%' }}
              className="focusable"
            />
            <input
              placeholder="valor (aceita {{tokens}})"
              value={row.v}
              onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, v: e.target.value } : r)))}
              style={{ ...inputStyle, flex: 1 }}
              className="focusable mono"
            />
            <button
              onClick={() => onChange(rows.filter((_, j) => j !== i))}
              className="focusable"
              title="Remover"
              style={{ ...inputStyle, width: 34, cursor: 'pointer', color: 'var(--danger)' }}
            >
              ×
            </button>
          </div>
        ))}
        <Button variant="ghost" size="sm" icon="plus" onClick={() => onChange([...rows, { k: '', v: '' }])}>
          Adicionar
        </Button>
      </div>
    </div>
  );
}

/** Acessa um caminho `a.b[0].c` num objeto, p/ pré-visualizar o mapeamento. */
function getByPath(obj: unknown, path: string): unknown {
  if (!path) return undefined;
  const tokens: (string | number)[] = [];
  let buf = '';
  for (let i = 0; i < path.length; i++) {
    const c = path[i]!;
    if (c === '.') {
      if (buf) tokens.push(buf), (buf = '');
    } else if (c === '[') {
      if (buf) tokens.push(buf), (buf = '');
      const close = path.indexOf(']', i);
      tokens.push(Number(path.slice(i + 1, close)));
      i = close;
    } else buf += c;
  }
  if (buf) tokens.push(buf);
  let cur: any = obj;
  for (const t of tokens) {
    if (cur == null) return undefined;
    if (t === 'length' && Array.isArray(cur)) cur = cur.length;
    else cur = cur[t as any];
  }
  return cur;
}

/** Lista os caminhos de campo de um item (escalares de topo + 1 nível aninhado). */
function leafPaths(obj: unknown, prefix = '', depth = 2): string[] {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return prefix ? [prefix] : [];
  const out: string[] = [];
  for (const k of Object.keys(obj as Record<string, unknown>)) {
    const p = prefix ? `${prefix}.${k}` : k;
    const v = (obj as Record<string, unknown>)[k];
    if (v && typeof v === 'object' && !Array.isArray(v) && depth > 1) out.push(...leafPaths(v, p, depth - 1));
    else out.push(p);
  }
  return out;
}

export interface ProviderExisting {
  id: string;
  slug: string;
  categorias_aceitas: string[];
  ordem_prioridade: number;
  config_json: any;
}

function objToKV(o: unknown): KV[] {
  if (!o || typeof o !== 'object') return [];
  return Object.entries(o as Record<string, unknown>).map(([k, v]) => ({ k, v: String(v) }));
}

export function FetchWizard({
  onClose,
  onCreated,
  existing
}: {
  onClose: () => void;
  onCreated: () => void;
  existing?: ProviderExisting;
}) {
  const { apiFetch } = useAuth();
  const toast = useToast();
  const isEdit = !!existing;
  // config_json pode vir como string (jsonb) — parse defensivo.
  const cfg0 =
    typeof existing?.config_json === 'string'
      ? safeParse(existing.config_json)
      : (existing?.config_json ?? {});
  const ep0: any = (cfg0 as any).endpoint ?? {};
  const auth0: any = (cfg0 as any).auth ?? { type: 'none' };
  const req0: any = (cfg0 as any).request ?? {};
  const resp0: any = (cfg0 as any).response ?? {};
  const map0: any = resp0.item_mapping ?? {};

  const [step, setStep] = useState(1);

  // Identidade
  const [slug, setSlug] = useState(existing?.slug ?? '');
  const [categorias, setCategorias] = useState<string[]>(existing?.categorias_aceitas ?? ['image']);
  const [ordem, setOrdem] = useState(existing?.ordem_prioridade ?? 30);

  // Passo 1 — enviar
  const [url, setUrl] = useState<string>(ep0.url ?? 'https://');
  const [method, setMethod] = useState<'GET' | 'POST'>(ep0.method === 'POST' ? 'POST' : 'GET');
  const [authType, setAuthType] = useState<'none' | 'header' | 'query'>(auth0.type ?? 'none');
  const [authKey, setAuthKey] = useState<string>(auth0.header ?? auth0.param ?? 'Authorization');
  const [authValue, setAuthValue] = useState<string>(auth0.value ?? '{{env.SUA_CHAVE}}');
  const [queryParams, setQueryParams] = useState<KV[]>(
    isEdit ? objToKV(req0.query_params) : [{ k: 'query', v: "{{request.keywords | join:' '}}" }]
  );
  const [headers, setHeaders] = useState<KV[]>(objToKV(req0.headers));
  const [bodyJson, setBodyJson] = useState<string>(req0.body_json ? JSON.stringify(req0.body_json, null, 2) : '');

  // Passo 2 — executar
  const [sampleKeyword, setSampleKeyword] = useState('praia');
  const [sampleQtd, setSampleQtd] = useState(2);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<{ status?: number; json?: unknown; error?: string } | null>(null);

  // Passo 3 — mapear resposta
  const [listPath, setListPath] = useState<string>(resp0.list_path ?? '');
  const [mapUrl, setMapUrl] = useState<string>(map0.url_arquivo ?? '');
  const [mapDescricao, setMapDescricao] = useState<string>(map0.descricao ?? '');
  const [mapExtensao, setMapExtensao] = useState<string>(map0.extensao ?? 'jpg');
  const [mapPreview, setMapPreview] = useState<string>(map0.url_preview ?? '');
  const [saving, setSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  function buildConfig(includeResponse: boolean) {
    let host = '';
    try {
      host = new URL(url).hostname;
    } catch {
      /* ignore */
    }
    const cfg: any = {
      endpoint: { url, method, ...(host ? { allowed_hosts: [host] } : {}) },
      auth:
        authType === 'header'
          ? { type: 'header', header: authKey, value: authValue }
          : authType === 'query'
            ? { type: 'query', param: authKey, value: authValue }
            : { type: 'none' },
      request: {
        query_params: kvToObj(queryParams),
        headers: kvToObj(headers),
        ...(method !== 'GET' && bodyJson.trim() ? { body_json: safeParse(bodyJson) } : {})
      }
    };
    if (includeResponse) {
      cfg.response = {
        list_path: listPath,
        item_mapping: {
          url_arquivo: mapUrl,
          tipo: categorias[0] ?? 'image',
          extensao: mapExtensao,
          ...(mapDescricao ? { descricao: mapDescricao } : {}),
          ...(mapPreview ? { url_preview: mapPreview } : {})
        }
      };
    }
    return cfg;
  }

  async function runPreview() {
    setPreviewing(true);
    setPreview(null);
    const r = await apiFetch<{ ok: boolean; status?: number; json?: unknown; error?: string }>(
      '/v1/admin/providers/preview-fetch',
      {
        method: 'POST',
        body: {
          config_json: buildConfig(false),
          sample: { keywords: [sampleKeyword], tipo: categorias[0] ?? 'image', quantidade: sampleQtd }
        }
      }
    );
    setPreviewing(false);
    if (!r.ok || !r.data) {
      setPreview({ error: r.error?.message ?? 'Falha na chamada' });
      return;
    }
    if (!r.data.ok) {
      setPreview({ error: r.data.error ?? 'Erro ao executar' });
      return;
    }
    setPreview({ status: r.data.status, json: r.data.json });
  }

  async function save() {
    setSaving(true);
    setSaveErrors([]);
    const config_json = buildConfig(true);
    const v = await apiFetch<{ ok: boolean; errors: string[] }>('/v1/admin/providers/validate-schema', {
      method: 'POST',
      body: { tipo: 'fetch', config_json }
    });
    if (v.ok && v.data && !v.data.ok) {
      setSaving(false);
      setSaveErrors(v.data.errors);
      return;
    }
    const r = isEdit
      ? await apiFetch<{ ok: boolean }>(`/v1/admin/providers/${existing!.id}`, {
          method: 'PATCH',
          body: { categorias_aceitas: categorias, ordem_prioridade: ordem, config_json }
        })
      : await apiFetch<{ ok: boolean }>('/v1/admin/providers/', {
          method: 'POST',
          body: { slug, tipo: 'fetch', categorias_aceitas: categorias, ordem_prioridade: ordem, ativo: false, config_json }
        });
    setSaving(false);
    if (!r.ok) {
      setSaveErrors([r.error?.message ?? 'Falha ao salvar']);
      return;
    }
    toast(isEdit ? `Provider "${slug}" atualizado.` : `Provider "${slug}" criado (inativo). Ative quando quiser.`, 'success');
    onCreated();
    onClose();
  }

  // sugere arrays do JSON p/ list_path
  const arraySuggestions = useMemo(() => {
    const out: string[] = [];
    const j: any = preview?.json;
    if (Array.isArray(j)) out.push('');
    if (j && typeof j === 'object') for (const k of Object.keys(j)) if (Array.isArray(j[k])) out.push(k);
    return out;
  }, [preview]);

  const firstItem = useMemo(() => {
    const list = getByPath(preview?.json, listPath);
    const arr = Array.isArray(list) ? list : Array.isArray(preview?.json) ? (preview?.json as any[]) : null;
    return arr && arr.length ? arr[0] : null;
  }, [preview, listPath]);

  // Campos disponíveis no 1º item (chips clicáveis para mapear).
  const fieldPaths = useMemo(() => leafPaths(firstItem), [firstItem]);

  // Ao chegar o preview, auto-seleciona o list_path: raiz-array → ""; senão, se houver
  // exatamente um array aninhado, usa-o.
  useEffect(() => {
    if (!preview?.json || listPath !== '') return;
    if (Array.isArray(preview.json)) return; // raiz já é a lista ("" correto)
    const keys = arraySuggestions.filter((s) => s !== '');
    if (keys.length === 1) setListPath(keys[0]!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const step1Valid = slug.trim().length >= 2 && /^https?:\/\/.+/.test(url) && categorias.length > 0;
  const canSave = step1Valid && listPath !== undefined && mapUrl.trim() !== '';

  return (
    <Modal open onClose={onClose} title={isEdit ? `Editar provider · ${slug}` : 'Novo provider · Fetch'} subtitle={`Passo ${step} de 3`} width={720}>
      {/* stepper */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {['Enviar', 'Executar', 'Mapear'].map((lbl, i) => {
          const n = i + 1;
          const active = n === step;
          const done = n < step;
          return (
            <button
              key={lbl}
              onClick={() => setStep(n)}
              className="focusable"
              style={{
                flex: 1,
                padding: '7px 10px',
                borderRadius: 'var(--r-sm)',
                fontSize: 12.5,
                fontWeight: 600,
                textAlign: 'center',
                cursor: 'pointer',
                background: active ? 'var(--brand-tint)' : done ? 'var(--bg-2)' : 'var(--bg-0)',
                color: active ? 'var(--brand-strong)' : done ? 'var(--text-1)' : 'var(--text-2)',
                border: '1px solid var(--border)'
              }}
            >
              {n}. {lbl}
            </button>
          );
        })}
      </div>

      {/* ===== PASSO 1 ===== */}
      {step === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Slug (id único)</label>
              <input value={slug} disabled={isEdit} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} placeholder="pexels" style={{ ...inputStyle, opacity: isEdit ? 0.6 : 1 }} className="focusable mono" />
            </div>
            <div style={{ width: 110 }}>
              <label style={labelStyle}>Ordem</label>
              <input type="number" value={ordem} onChange={(e) => setOrdem(Number(e.target.value))} style={inputStyle} className="focusable" />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Categorias aceitas</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {FILE_TYPES.map((t) => {
                const on = categorias.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => setCategorias((c) => (on ? c.filter((x) => x !== t) : [...c, t]))}
                    style={{
                      padding: '5px 11px',
                      fontSize: 12.5,
                      borderRadius: 999,
                      cursor: 'pointer',
                      border: '1px solid var(--border)',
                      background: on ? 'var(--brand-tint)' : 'var(--bg-0)',
                      color: on ? 'var(--brand-strong)' : 'var(--text-2)'
                    }}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ width: 110 }}>
              <label style={labelStyle}>Método</label>
              <select value={method} onChange={(e) => setMethod(e.target.value as any)} style={inputStyle} className="focusable">
                <option>GET</option>
                <option>POST</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Endpoint URL</label>
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://api.exemplo.com/search" style={inputStyle} className="focusable mono" />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Autenticação</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={authType} onChange={(e) => setAuthType(e.target.value as any)} style={{ ...inputStyle, width: 120 }} className="focusable">
                <option value="none">Nenhuma</option>
                <option value="header">Header</option>
                <option value="query">Query param</option>
              </select>
              {authType !== 'none' && (
                <>
                  <input value={authKey} onChange={(e) => setAuthKey(e.target.value)} placeholder={authType === 'header' ? 'Authorization' : 'api_key'} style={{ ...inputStyle, flex: '0 0 30%' }} className="focusable mono" />
                  <input value={authValue} onChange={(e) => setAuthValue(e.target.value)} placeholder="{{env.SUA_CHAVE}}" style={{ ...inputStyle, flex: 1 }} className="focusable mono" />
                </>
              )}
            </div>
            {authType !== 'none' && (
              <p style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 4 }}>
                Use <code className="mono">{'{{env.NOME}}'}</code> — a chave fica no <code className="mono">.env</code>, nunca no banco.
              </p>
            )}
          </div>

          <KVEditor rows={queryParams} onChange={setQueryParams} label="Query params" />
          <KVEditor rows={headers} onChange={setHeaders} label="Headers" />
          {method === 'POST' && (
            <div>
              <label style={labelStyle}>Body (JSON, aceita tokens)</label>
              <textarea value={bodyJson} onChange={(e) => setBodyJson(e.target.value)} rows={4} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} placeholder={`{ "q": "{{request.keywords | join:' '}}" }`} />
            </div>
          )}
          <p style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
            Tokens: <code className="mono">{'{{request.keywords | join:\' \'}}'}</code>, <code className="mono">{'{{request.quantidade}}'}</code>, <code className="mono">{'{{request.descricao}}'}</code>, <code className="mono">{'{{env.X}}'}</code>
          </p>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button variant="brand" disabled={!step1Valid} onClick={() => setStep(2)}>Próximo →</Button>
          </div>
        </div>
      )}

      {/* ===== PASSO 2 ===== */}
      {step === 2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--text-2)' }}>
            Execute a chamada com valores de exemplo e veja o que a API devolve.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Keyword de exemplo</label>
              <input value={sampleKeyword} onChange={(e) => setSampleKeyword(e.target.value)} style={inputStyle} className="focusable" />
            </div>
            <div style={{ width: 110 }}>
              <label style={labelStyle}>Quantidade</label>
              <input type="number" value={sampleQtd} onChange={(e) => setSampleQtd(Number(e.target.value))} style={inputStyle} className="focusable" />
            </div>
            <Button variant="brand" icon="activity" onClick={runPreview} disabled={previewing}>
              {previewing ? 'Executando…' : 'Executar'}
            </Button>
          </div>

          {preview?.error && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)', borderRadius: 'var(--r-sm)', padding: '10px 12px', fontSize: 12.5 }}>
              {preview.error}
            </div>
          )}
          {preview?.json != null && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Badge color={preview.status && preview.status < 300 ? 'success' : 'warning'} mono size="sm">HTTP {preview.status}</Badge>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>resposta crua</span>
              </div>
              <pre style={{ background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '10px 12px', fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', margin: 0, overflow: 'auto', maxHeight: 280, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(preview.json, null, 2)}
              </pre>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <Button variant="ghost" onClick={() => setStep(1)}>← Voltar</Button>
            <Button variant="brand" disabled={!preview?.json && !isEdit} onClick={() => setStep(3)}>Próximo →</Button>
          </div>
        </div>
      )}

      {/* ===== PASSO 3 ===== */}
      {step === 3 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--text-2)' }}>
            Diga onde está a lista e qual campo é a <b>URL do arquivo</b> a baixar.
          </p>
          <div>
            <label style={labelStyle}>Caminho da lista (list_path)</label>
            <input value={listPath} onChange={(e) => setListPath(e.target.value)} placeholder='ex.: photos  (vazio = a raiz já é a lista)' style={inputStyle} className="focusable mono" />
            {arraySuggestions.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>sugestões:</span>
                {arraySuggestions.map((s) => (
                  <button key={s || '(raiz)'} onClick={() => setListPath(s)} style={{ fontSize: 11.5, padding: '2px 8px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text-1)', cursor: 'pointer' }}>
                    {s || '(raiz)'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {firstItem ? (
            <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '8px 10px' }}>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 4 }}>1º item da lista — clique num campo abaixo para mapear:</div>
              <pre style={{ margin: 0, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', maxHeight: 130, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(firstItem, null, 2)}
              </pre>
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
              Defina o <b>list_path</b> acima para o assistente achar o 1º item e sugerir os campos.
            </div>
          )}

          <MapField label="Campo da URL do arquivo (obrigatório)" value={mapUrl} onChange={setMapUrl} placeholder="ex.: src.original ou url" item={firstItem} suggestions={fieldPaths} />
          <MapField label="Campo da descrição (opcional)" value={mapDescricao} onChange={setMapDescricao} placeholder="ex.: alt ou title" item={firstItem} suggestions={fieldPaths} />
          <MapField label="Campo do preview/thumbnail (opcional)" value={mapPreview} onChange={setMapPreview} placeholder="ex.: src.medium" item={firstItem} suggestions={fieldPaths} />
          <div style={{ width: 140 }}>
            <label style={labelStyle}>Extensão padrão</label>
            <input value={mapExtensao} onChange={(e) => setMapExtensao(e.target.value)} placeholder="jpg" style={inputStyle} className="focusable mono" />
          </div>

          {saveErrors.length > 0 && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid color-mix(in oklch, var(--danger) 30%, transparent)', borderRadius: 'var(--r-sm)', padding: '10px 12px', fontSize: 12.5 }}>
              <ul style={{ margin: 0, paddingLeft: 16 }}>{saveErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <Button variant="ghost" onClick={() => setStep(2)}>← Voltar</Button>
            <Button variant="brand" disabled={!canSave || saving} onClick={save}>
              {saving ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Salvar provider (inativo)'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function MapField({
  label,
  value,
  onChange,
  placeholder,
  item,
  suggestions = []
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  item: unknown;
  suggestions?: string[];
}) {
  const resolved = value ? getByPath(item, value) : undefined;
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} className="focusable mono" />
      {suggestions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
          {suggestions.map((s) => {
            const active = s === value;
            return (
              <button
                key={s}
                onClick={() => onChange(s)}
                className="mono"
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  borderRadius: 999,
                  cursor: 'pointer',
                  border: '1px solid var(--border)',
                  background: active ? 'var(--brand-tint)' : 'var(--bg-0)',
                  color: active ? 'var(--brand-strong)' : 'var(--text-2)'
                }}
              >
                {s}
              </button>
            );
          })}
        </div>
      )}
      {value && (
        <p style={{ fontSize: 11.5, color: resolved != null ? 'var(--success)' : 'var(--text-3)', marginTop: 3 }}>
          {resolved != null ? `→ ${String(resolved).slice(0, 80)}` : '→ (não encontrado no 1º item)'}
        </p>
      )}
    </div>
  );
}

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return {};
  }
}
